import { useCallback, useEffect, useState } from 'react';

import { request } from '../../shared/api';
import {
  countFilters,
  loadFilters,
  saveFilters,
  toMinor,
  toRubles,
  toWhole,
  toggle,
  withField,
  type ChoiceField,
  type Filters,
} from '../../shared/filters';
import { label } from '../../shared/labels';
import { ListSkeleton, renderFailure } from '../../shared/states';
import { useLoad } from '../../shared/use-load';
import { useUpgrade } from '../../shared/upgrade';
import { useOpened } from '../../shared/use-opened';
import { ListingScreen } from '../listing/ListingScreen';
import { Picker, type Option } from './Picker';
import { ResultsScreen } from './ResultsScreen';
import { SubscribeCard } from './SubscribeCard';

/**
 * Фильтры поиска (T11.5, `02_SPEC.md` §8).
 *
 * Все фильтры спецификации на одном экране. Он длинный, и это верно:
 * подбор автомобиля — работа, а не развлечение, и прятать половину
 * условий под «расширенный поиск» значит заставлять их там искать.
 *
 * ## Порядок — по частоте, а не по алфавиту
 *
 * Сверху то, что задают всегда: марка, цена, год. Ниже то, чем уточняют:
 * пробег, регион, владельцы. В конце редкое: отклонение от рынка,
 * наличие VIN. Алфавит означал бы, что «Год» ищут после «Владельцев».
 *
 * ## Каждое изменение сразу в хранилище
 *
 * Не по кнопке «применить»: Telegram выгружает приложение, когда человек
 * ушёл в переписку, и несохранённый набор пропал бы вместе с ним.
 */

interface Named {
  readonly id: number;
  readonly name: string;
}

interface Generation {
  readonly id: number;
  readonly name: string;
  readonly year_from: number | null;
  readonly year_to: number | null;
}

interface Meta {
  readonly body: string[];
  readonly gearbox: string[];
  readonly drive: string[];
  readonly engine_type: string[];
}

interface Dictionaries {
  readonly brands: Named[];
  readonly regions: Named[];
  readonly meta: Meta;
}

/** Поле с подписью. Подпись сверху: она короче значения. */
function Field({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <label className="field">
      <span className="field__title">{title}</span>
      {children}
    </label>
  );
}

/** Пара «от» и «до» в строку: диапазон читается целиком. */
function Range({
  title,
  from,
  to,
  onFrom,
  onTo,
}: {
  title: string;
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
}): React.JSX.Element {
  return (
    <div className="field">
      <span className="field__title">{title}</span>
      <div className="field__row">
        <input
          className="input"
          inputMode="numeric"
          placeholder="от"
          value={from}
          onChange={(event) => {
            onFrom(event.target.value);
          }}
        />
        <input
          className="input"
          inputMode="numeric"
          placeholder="до"
          value={to}
          onChange={(event) => {
            onTo(event.target.value);
          }}
        />
      </div>
    </div>
  );
}

/** Выбор нескольких значений нажатием: список короткий, и он нагляднее выпадающего. */
function Choices({
  title,
  field,
  values,
  filters,
  onToggle,
}: {
  title: string;
  field: ChoiceField;
  values: readonly string[];
  filters: Filters;
  onToggle: (field: ChoiceField, value: string) => void;
}): React.JSX.Element {
  const chosen = filters[field] ?? [];

  return (
    <div className="field">
      <span className="field__title">{title}</span>
      <div className="chips">
        {values.map((value) => (
          <button
            key={value}
            type="button"
            className="chip"
            aria-pressed={chosen.includes(value)}
            onClick={() => {
              onToggle(field, value);
            }}
          >
            {label(value)}
          </button>
        ))}
      </div>
    </div>
  );
}

async function loadDictionaries(): Promise<Dictionaries> {
  // Три запроса разом, а не по очереди: они не зависят друг от друга, и
  // последовательность утроила бы ожидание на медленной связи
  const [brands, regions, meta] = await Promise.all([
    request<Named[]>('/v1/dictionaries/brands'),
    request<Named[]>('/v1/dictionaries/regions'),
    request<Meta>('/v1/dictionaries/meta'),
  ]);

  return { brands, regions, meta };
}

/** Справочник в вид, удобный строке выдачи: номер к названию. */
function byId(items: readonly Named[]): Map<number, string> {
  return new Map(items.map((one) => [one.id, one.name]));
}

/** Годы поколения строкой: «2011–2019», «с 2019». */
function years(one: Generation): string | undefined {
  if (one.year_from === null && one.year_to === null) return undefined;
  if (one.year_to === null) return `с ${one.year_from}`;
  if (one.year_from === null) return `по ${one.year_to}`;

  return `${one.year_from}–${one.year_to}`;
}

/**
 * Поля, спрятанные под «Расширенный фильтр».
 *
 * Перечень нужен ради счётчика рядом с кнопкой: свёрнутый раздел,
 * молчащий о том, что внутри что-то выбрано, — это поиск, который
 * необъяснимо ничего не находит.
 */
export const ADVANCED_FIELDS = [
  'body',
  'gearbox',
  'drive',
  'engine_type',
  'seller_type',
  'sources',
] as const;

/**
 * Регионы, где машин больше всего, — наверх списка.
 *
 * Восемьдесят три региона в алфавитном порядке начинаются с Адыгеи, а
 * ищут в подавляющем большинстве случаев не её. Человеку, открывшему
 * список, первым делом показывается то, что он скорее всего и выберет,
 * — и ему не приходится ни листать, ни набирать.
 *
 * Порядок внутри тройки не алфавитный, а по числу объявлений: столица,
 * область вокруг неё, вторая столица.
 */
export const PINNED_REGIONS: readonly string[] = [
  'Москва',
  'Московская область',
  'Санкт-Петербург',
];

export function pinned(regions: readonly Named[]): Named[] {
  const first: Named[] = [];

  for (const name of PINNED_REGIONS) {
    const found = regions.find((one) => one.name === name);

    // Пропуск, а не пустая строка: справочник — не наша забота, и
    // переименуй кто-нибудь регион, список обязан остаться рабочим
    if (found !== undefined) first.push(found);
  }

  const rest = regions.filter((one) => !first.includes(one));

  return [...first, ...rest];
}

export function FiltersScreen(): React.JSX.Element {
  const [filters, setFilters] = useState<Filters>(loadFilters);
  const [state, retry] = useLoad<Dictionaries>(useCallback(loadDictionaries, []));
  const onUpgrade = useUpgrade();
  const [models, setModels] = useState<Option[]>([]);
  // Раскрытость живёт вместе с экраном, а не с каждой правкой:
  // закрывайся раздел после выбора — кузов и коробка стоили бы по
  // два лишних нажатия каждый
  const [advanced, setAdvanced] = useState(false);
  const [generations, setGenerations] = useState<Option[]>([]);
  // Открытое объявление. Отдельного маршрута нет намеренно: Telegram не
  // даёт приложению адресной строки, а возврат «назад» человек ждёт от
  // кнопки в самой карточке. Фильтры при этом остаются набранными —
  // они живут в состоянии экрана, а не в адресе.
  //
  // Правило возврата общее с избранным и историей уведомлений
  // (`shared/use-opened.ts`): три его копии разошлись бы, и заметил бы
  // это только пользователь
  const { opened, open, back, flow } = useOpened();

  const brandId = filters.brand_ids?.[0];
  const modelId = filters.model_ids?.[0];

  // Модели грузятся по выбранной марке, а не все сразу: их десятки тысяч,
  // и список без марки бесполезен — выбирать в нём нечего
  useEffect(() => {
    let alive = true;

    if (brandId === undefined) {
      setModels([]);

      return () => {
        alive = false;
      };
    }

    request<Option[]>(`/v1/dictionaries/brands/${brandId}/models`)
      .then((found) => {
        if (alive) setModels(found);
      })
      .catch(() => {
        // Отказ справочника не должен ронять экран: прочие фильтры
        // работают, а модель человек задаст позже
        if (alive) setModels([]);
      });

    return () => {
      alive = false;
    };
  }, [brandId]);

  useEffect(() => {
    let alive = true;

    if (modelId === undefined) {
      setGenerations([]);

      return () => {
        alive = false;
      };
    }

    request<Generation[]>(`/v1/dictionaries/models/${modelId}/generations`)
      .then((found) => {
        if (alive)
          setGenerations(found.map((one) => ({ id: one.id, name: one.name, note: years(one) })));
      })
      .catch(() => {
        if (alive) setGenerations([]);
      });

    return () => {
      alive = false;
    };
  }, [modelId]);

  /** Изменение и сохранение одним действием: несохранённого состояния не бывает. */
  const change = useCallback((next: Filters) => {
    setFilters(next);
    saveFilters(next);
  }, []);

  const set = useCallback(
    <K extends keyof Filters>(field: K, value: Filters[K] | undefined) => {
      change(withField(filters, field, value));
    },
    [change, filters],
  );

  const flip = useCallback(
    (field: ChoiceField, value: string) => {
      change(toggle(filters, field, value));
    },
    [change, filters],
  );

  if (state.kind === 'loading') return <ListSkeleton rows={4} />;

  if (state.kind === 'failed') {
    return <>{renderFailure(state.error, { onRetry: retry, onUpgrade })}</>;
  }

  const { brands, regions, meta } = state.value;
  const ordered = pinned(regions);
  const chosen = countFilters(filters);
  const advancedCount = ADVANCED_FIELDS.reduce(
    (sum, field) => sum + (filters[field]?.length ?? 0),
    0,
  );

  return (
    <>
      {opened === null ? null : <ListingScreen listingId={opened} onBack={back} />}

      {/*
        Поиск не размонтируется, а прячется. Убери его из дерева — и
        вместе с ним пропадут загруженные страницы выдачи: возврат из
        карточки начинал бы поиск заново, с первой страницы и с начала
        списка. `display: contents` не даёт обёртке вмешаться в
        раскладку экрана, пока она видима.
      */}
      <div className={flow}>
        <Picker
          title="Марка"
          options={brands}
          value={brandId}
          placeholder="начните вводить: BMW, Toyota…"
          emptyLabel="любая"
          onPick={(id) => {
            // Смена марки сбрасывает модель и поколение: они принадлежали
            // прежней марке, и оставленные превратили бы поиск в заведомо
            // пустой
            change(
              withField(
                withField(
                  withField(filters, 'brand_ids', id === undefined ? undefined : [id]),
                  'model_ids',
                  undefined,
                ),
                'generation_ids',
                undefined,
              ),
            );
          }}
        />

        <Picker
          title="Модель"
          options={models}
          value={modelId}
          placeholder="начните вводить название"
          emptyLabel={brandId === undefined ? 'сначала выберите марку' : 'любая'}
          disabled={brandId === undefined}
          onPick={(id) => {
            change(
              withField(
                withField(filters, 'model_ids', id === undefined ? undefined : [id]),
                'generation_ids',
                undefined,
              ),
            );
          }}
        />

        <Picker
          title="Поколение"
          options={generations}
          value={filters.generation_ids?.[0]}
          placeholder="начните вводить"
          emptyLabel={modelId === undefined ? 'сначала выберите модель' : 'любое'}
          disabled={modelId === undefined}
          onPick={(id) => {
            set('generation_ids', id === undefined ? undefined : [id]);
          }}
        />

        <Range
          title="Цена, ₽"
          from={toRubles(filters.price_min_minor)}
          to={toRubles(filters.price_max_minor)}
          onFrom={(value) => {
            set('price_min_minor', toMinor(value));
          }}
          onTo={(value) => {
            set('price_max_minor', toMinor(value));
          }}
        />

        <Range
          title="Год"
          from={String(filters.year_min ?? '')}
          to={String(filters.year_max ?? '')}
          onFrom={(value) => {
            set('year_min', toWhole(value));
          }}
          onTo={(value) => {
            set('year_max', toWhole(value));
          }}
        />

        <Field title="Пробег, км — не более">
          <input
            className="input"
            inputMode="numeric"
            placeholder="например, 100000"
            value={String(filters.mileage_max_km ?? '')}
            onChange={(event) => {
              set('mileage_max_km', toWhole(event.target.value));
            }}
          />
        </Field>

        {/*
          Регионов можно выбрать несколько: машину ищут не в одной
          области, а там, куда готовы поехать — Москва и Подмосковье,
          Петербург и Ленинградская. Отбор по списку регионов был на
          стороне API с самого начала, ограничение жило только здесь.
        */}
        <Picker
          title="Регион"
          options={ordered}
          values={filters.region_ids ?? []}
          placeholder="начните вводить: Моск, Питер, Краснодар…"
          emptyLabel="любой"
          onPick={(id) => {
            const current = filters.region_ids ?? [];
            // Пусто означает «сбросить все», а не «добавить ничего»
            const next =
              id === undefined
                ? []
                : current.includes(id)
                  ? current.filter((each) => each !== id)
                  : [...current, id];

            // Смена регионов сбрасывает города: они принадлежали прежним,
            // и оставленные дали бы заведомо пустой поиск
            change(
              withField(
                withField(filters, 'region_ids', next.length === 0 ? undefined : next),
                'city_ids',
                undefined,
              ),
            );
          }}
        />

        <Field title="Владельцев — не более">
          <input
            className="input"
            inputMode="numeric"
            placeholder="например, 2"
            value={String(filters.owners_max ?? '')}
            onChange={(event) => {
              set('owners_max', toWhole(event.target.value));
            }}
          />
        </Field>

        {/*
          Шесть наборов под одной кнопкой. Порознь они занимали половину
          экрана и стояли между «Регионом» и «Ниже рынка» — то есть
          между тем, что заполняют почти всегда, и тем, ради чего сюда
          и приходят. Человек пролистывал их каждый раз, а заполнял
          редко.

          Раскрытые остаются раскрытыми, пока экран жив: закрой мы их
          после каждой правки — выбор кузова и коробки превратился бы в
          два лишних нажатия на каждое поле.
        */}
        <button
          type="button"
          className="button button--quiet advanced__toggle"
          onClick={() => {
            setAdvanced((was) => !was);
          }}
        >
          {advanced ? 'Скрыть расширенный фильтр' : 'Расширенный фильтр'}
          {advancedCount === 0 ? '' : `  ·  ${String(advancedCount)}`}
        </button>

        {advanced ? (
          <>
            <Choices
              title="Кузов"
              field="body"
              values={meta.body}
              filters={filters}
              onToggle={flip}
            />
            <Choices
              title="Коробка"
              field="gearbox"
              values={meta.gearbox}
              filters={filters}
              onToggle={flip}
            />
            <Choices
              title="Привод"
              field="drive"
              values={meta.drive}
              filters={filters}
              onToggle={flip}
            />
            <Choices
              title="Двигатель"
              field="engine_type"
              values={meta.engine_type}
              filters={filters}
              onToggle={flip}
            />
            <Choices
              title="Продавец"
              field="seller_type"
              values={['private', 'dealer', 'unknown']}
              filters={filters}
              onToggle={flip}
            />
            <Choices
              title="Площадки"
              field="sources"
              values={['avito', 'autoru', 'drom']}
              filters={filters}
              onToggle={flip}
            />
          </>
        ) : null}

        <Field title="Ниже рынка на, % — не менее">
          <input
            className="input"
            inputMode="numeric"
            placeholder="например, 10"
            value={String(filters.market_delta_max_pct ?? '')}
            onChange={(event) => {
              set('market_delta_max_pct', toWhole(event.target.value));
            }}
          />
        </Field>

        <label className="field field--inline">
          <input
            type="checkbox"
            checked={filters.has_vin === true}
            onChange={(event) => {
              set('has_vin', event.target.checked ? true : undefined);
            }}
          />
          <span className="field__title">Только с VIN</span>
        </label>

        <div className="field">
          <button
            type="button"
            className="button"
            disabled={chosen === 0}
            onClick={() => {
              change({});
            }}
          >
            {chosen === 0 ? 'Фильтры не заданы' : `Сбросить (${chosen})`}
          </button>
        </div>

        {/*
        Подписка стоит над выдачей, а не под ней. Решение подписаться
        принимают, посмотрев на условия, а не пролистав два десятка
        объявлений; внизу кнопку пришлось бы искать прокруткой.
      */}
        <SubscribeCard
          filters={filters}
          chosen={{
            brand: brands.find((one) => one.id === brandId)?.name,
            model: models.find((one) => one.id === modelId)?.name,
            generation: generations.find((one) => one.id === filters.generation_ids?.[0])?.name,
          }}
        />

        {/*
        Выдача под фильтрами, а не за кнопкой «искать». Кнопка нужна
        там, где запрос стоит дорого и его нельзя отменить; здесь он
        дешёвый, а видеть отклик сразу — единственный способ понять,
        какое условие обнулило выдачу.
      */}
        <ResultsScreen
          filters={filters}
          brands={byId(brands)}
          regions={byId(regions)}
          onOpen={open}
        />
      </div>
    </>
  );
}
